import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logging';

/**
 * Avatar display data derived from user profile
 */
export interface AvatarDisplayData {
  /** URL of the profile image or null if not set */
  imageUrl: string | null;
  /** User's initials for fallback display */
  initials: string;
  /** User's full name for aria-label */
  fullName: string;
  /** Whether the avatar image is currently loading */
  isLoading: boolean;
  /** Error state if profile fetch failed */
  error: Error | null;
}

/**
 * Generate initials from first and last name
 * Handles edge cases: missing names, special characters, long names
 * 
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @returns Initials (1-2 uppercase letters)
 */
function generateInitials(firstName: string | null, lastName: string | null): string {
  // Handle completely missing names
  if (!firstName && !lastName) {
    return 'U'; // Default to 'U' for User
  }

  // Clean names: remove special chars, trim whitespace
  const cleanFirst = firstName?.trim().replace(/[^a-zA-Z\s]/g, '') || '';
  const cleanLast = lastName?.trim().replace(/[^a-zA-Z\s]/g, '') || '';

  // Get first character of each name
  const firstInitial = cleanFirst.charAt(0).toUpperCase();
  const lastInitial = cleanLast.charAt(0).toUpperCase();

  // Return both initials if available, otherwise single initial or default
  if (firstInitial && lastInitial) {
    return `${firstInitial}${lastInitial}`;
  }
  
  return firstInitial || lastInitial || 'U';
}

/**
 * Hook for fetching and managing user profile avatar data
 * 
 * Features:
 * - Fetches profile image URL from database
 * - Generates initials fallback from user's name
 * - Provides loading and error states
 * - Caches data for 10 minutes (stale time)
 * - Refetches on window focus and reconnect
 * 
 * @returns Avatar display data with loading/error states
 * 
 * @example
 * ```tsx
 * function Header() {
 *   const { imageUrl, initials, fullName, isLoading } = useProfileAvatar();
 *   
 *   return (
 *     <Avatar>
 *       {imageUrl ? (
 *         <AvatarImage src={imageUrl} alt={`Profile picture of ${fullName}`} />
 *       ) : null}
 *       <AvatarFallback>{initials}</AvatarFallback>
 *     </Avatar>
 *   );
 * }
 * ```
 */
export function useProfileAvatar(): AvatarDisplayData {
  const { user } = useAuth();

  // Fetch minimal profile data needed for avatar display
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile', 'avatar', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      logger.debug('Fetching profile avatar data', { 
        component: 'useProfileAvatar',
        userId: user.id 
      });

      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('profile_image, legal_first_name, legal_last_name')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        logger.error('Failed to fetch profile avatar', fetchError, {
          component: 'useProfileAvatar',
          userId: user.id
        });
        throw fetchError;
      }

      return data;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - avatar doesn't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes cache retention
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 2, // Retry failed fetches up to 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  // Memoize computed avatar data to avoid recalculation
  const avatarData = useMemo<AvatarDisplayData>(() => {
    const firstName = profile?.legal_first_name || null;
    const lastName = profile?.legal_last_name || null;
    const imageUrl = profile?.profile_image || null;

    // Generate initials for fallback
    const initials = generateInitials(firstName, lastName);

    // Create full name for accessibility
    const fullName = [firstName, lastName]
      .filter(Boolean)
      .join(' ')
      .trim() || 'User';

    return {
      imageUrl,
      initials,
      fullName,
      isLoading,
      error: error as Error | null,
    };
  }, [profile, isLoading, error]);

  return avatarData;
}
